import { Button } from '@/components/ui/button';
import {
   Card,
   CardContent,
   CardDescription,
   CardHeader,
   CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
   Form,
   FormControl,
   FormDescription,
   FormField,
   FormItem,
   FormLabel,
   FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
   Select,
   SelectContent,
   SelectItem,
   SelectTrigger,
   SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { coachService } from '@/lib/api/coachService';
import { PASSWORD_HINT, passwordField } from '@/lib/validation';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { Eye, EyeOff, Loader2, Shield, Sparkles, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { GetCity, GetCountries, GetState } from 'react-country-state-city';
import { useForm } from 'react-hook-form';
import { useLocation } from 'wouter';
import { z } from 'zod';

interface Country {
   id: number;
   name: string;
   emoji: string;
   iso2: string;
   iso3: string;
}

interface State {
   id: number;
   name: string;
   state_code: string;
}

interface City {
   id: number;
   name: string;
}

const expertiseOptions = [
   'Meditation',
   'Breathwork',
   'Movement',
   'Mindfulness',
   'Journaling',
   'Affirmations',
   'Yoga',
   'Sleep',
   'Stress Relief',
   'Dance Therapy',
   'Body Awareness',
   'Clinical Psychology',
   'Self-Discovery',
   'Creative Writing',
];

const languageOptions = [
   { code: 'en', name: 'English' },
   { code: 'es', name: 'Spanish' },
   { code: 'pt', name: 'Portuguese' },
   { code: 'de', name: 'German' },
   { code: 'fr', name: 'French' },
   { code: 'zh', name: 'Chinese' },
   { code: 'ko', name: 'Korean' },
];

const coachTypeLabels: Record<string, string> = {
   coach: 'Coach',
   verified_coach: 'Verified Coach',
   guest: 'Guest Contributor',
};

const acceptInvitationSchema = z
   .object({
      password: passwordField(),
      confirmPassword: z.string(),
      name: z.string().optional(),
      bio: z.string().optional(),
      expertise: z.array(z.string()).optional(),
      languages: z.array(z.string()).optional(),
      certifications: z.array(z.string()).optional(),
      country: z.string().optional(),
      state: z.string().optional(),
      city: z.string().optional(),
      avatar: z.instanceof(File).optional(),
   })
   .refine((data) => data.password === data.confirmPassword, {
      message: 'Passwords do not match',
      path: ['confirmPassword'],
   });

type AcceptInvitationFormData = z.infer<typeof acceptInvitationSchema>;

export default function AcceptInvitation() {
   const [location, setLocation] = useLocation();
   const { toast } = useToast();
   const [showPassword, setShowPassword] = useState(false);
   const [showConfirmPassword, setShowConfirmPassword] = useState(false);
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
   const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
   const [selectedState, setSelectedState] = useState<State | null>(null);
   const [selectedCity, setSelectedCity] = useState<City | null>(null);
   const [countries, setCountries] = useState<Country[]>([]);
   const [states, setStates] = useState<State[]>([]);
   const [cities, setCities] = useState<City[]>([]);
   const [countriesLoading, setCountriesLoading] = useState(false);
   const [statesLoading, setStatesLoading] = useState(false);
   const [citiesLoading, setCitiesLoading] = useState(false);
   const fileInputRef = useRef<HTMLInputElement>(null);

   // Extract token from query string
   const getTokenFromQuery = () => {
      const params = new URLSearchParams(window.location.search);
      return params.get('token');
   };

   const token = getTokenFromQuery();

   // Fetch invitation details to get email, coach type, and name
   const {
      data: invitationData,
      isLoading: isLoadingInvitation,
      error: invitationError,
   } = useQuery<{
      email: string;
      expiresAt: string;
      coachType?: string;
      name?: string;
   }>({
      queryKey: ['/coaches/validate-invitation', token],
      queryFn: async () => {
         if (!token) throw new Error('No token provided');
         try {
            return await coachService.validateInvitation(token);
         } catch (error: any) {
            throw new Error(
               error?.message || 'Failed to validate invitation token',
            );
         }
      },
      enabled: !!token,
      retry: false,
   });

   useEffect(() => {
      if (!token) {
         toast({
            title: 'Invalid invitation link',
            description:
               'Please check your invitation email for the correct link.',
            variant: 'destructive',
         });
         setLocation('/login');
      }
   }, [token, setLocation, toast]);

   useEffect(() => {
      if (invitationData?.email) {
         // Email is fetched and will be displayed as read-only
      }
   }, [invitationData]);

   // Fetch countries on mount
   useEffect(() => {
      setCountriesLoading(true);
      GetCountries()
         .then((result) => {
            setCountries(result);
         })
         .catch(() => {})
         .finally(() => setCountriesLoading(false));
   }, []);

   // Fetch states when country changes
   useEffect(() => {
      if (selectedCountry?.id) {
         setStates([]);
         setCities([]);
         setSelectedState(null);
         setSelectedCity(null);
         setStatesLoading(true);
         GetState(selectedCountry.id)
            .then((result) => {
               setStates(result);
            })
            .catch(() => {})
            .finally(() => setStatesLoading(false));
      } else {
         setStates([]);
         setCities([]);
      }
   }, [selectedCountry]);

   // Fetch cities when state changes
   useEffect(() => {
      if (selectedCountry?.id && selectedState?.id) {
         setCities([]);
         setSelectedCity(null);
         setCitiesLoading(true);
         GetCity(selectedCountry.id, selectedState.id)
            .then((result) => {
               setCities(result);
            })
            .catch(() => {})
            .finally(() => setCitiesLoading(false));
      } else {
         setCities([]);
      }
   }, [selectedCountry, selectedState]);

   useEffect(() => {
      // Handle validation errors
      if (invitationError) {
         const errorMessage =
            invitationError instanceof Error
               ? invitationError.message
               : 'Invalid or expired invitation token';

         toast({
            title: 'Invalid invitation',
            description:
               errorMessage.includes('expired') ||
               errorMessage.includes('Invalid')
                  ? 'This invitation link is no longer valid. Please contact support.'
                  : errorMessage,
            variant: 'destructive',
         });
         setLocation('/login');
      }
   }, [invitationError, toast, setLocation]);

   const form = useForm<AcceptInvitationFormData>({
      resolver: zodResolver(acceptInvitationSchema),
      defaultValues: {
         password: '',
         confirmPassword: '',
         name: '',
         bio: '',
         expertise: [],
         languages: [],
         certifications: [],
         country: '',
         state: '',
         city: '',
      },
   });

   const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
         form.setValue('avatar', file);
         const reader = new FileReader();
         reader.onloadend = () => {
            setAvatarPreview(reader.result as string);
         };
         reader.readAsDataURL(file);
      }
   };

   const onSubmit = async (data: AcceptInvitationFormData) => {
      if (!token) {
         toast({
            title: 'Invalid invitation token',
            description:
               'Please check your invitation email for the correct link.',
            variant: 'destructive',
         });
         setLocation('/login');
         return;
      }

      setIsSubmitting(true);
      try {
         await coachService.acceptInvitation({
            token,
            password: data.password,
            name: data.name || undefined,
            bio: data.bio || undefined,
            expertise:
               data.expertise && data.expertise.length > 0
                  ? data.expertise
                  : undefined,
            languages:
               data.languages && data.languages.length > 0
                  ? data.languages
                  : undefined,
            certifications:
               data.certifications && data.certifications.length > 0
                  ? data.certifications
                  : undefined,
            city: data.city || undefined,
            state: data.state || undefined,
            country: data.country || undefined,
            avatar: data.avatar,
         });

         toast({
            title: 'Invitation accepted successfully',
            description:
               'Your coach account has been created. You can now sign in.',
         });
         setLocation('/login');
      } catch (error: any) {
         const errorMessage =
            error?.message || 'Failed to accept invitation. Please try again.';

         if (
            errorMessage.includes('expired') ||
            errorMessage.includes('Invalid')
         ) {
            toast({
               title: 'Invalid or expired invitation',
               description:
                  'This invitation link is no longer valid. Please contact support.',
               variant: 'destructive',
            });
            setLocation('/login');
         } else {
            toast({
               title: 'Error accepting invitation',
               description: errorMessage,
               variant: 'destructive',
            });
         }
      } finally {
         setIsSubmitting(false);
      }
   };

   if (!token) {
      return null; // Will redirect in useEffect
   }

   if (isLoadingInvitation) {
      return (
         <div className='min-h-screen bg-background flex flex-col items-center justify-center p-4'>
            <div className='w-full max-w-2xl space-y-8'>
               <div className='text-center space-y-2'>
                  <div className='flex items-center justify-center gap-2 mb-4'>
                     <div className='w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center'>
                        <Sparkles className='w-5 h-5 text-primary animate-pulse' />
                     </div>
                  </div>
                  <h1 className='text-2xl font-semibold tracking-tight'>
                     Validating invitation...
                  </h1>
               </div>
            </div>
         </div>
      );
   }

   if (!invitationData) {
      return null; // Error handling will redirect
   }

   return (
      <div className='min-h-screen bg-background flex flex-col items-center justify-center p-4'>
         <div className='w-full max-w-2xl space-y-8'>
            <div className='text-center space-y-2'>
               <div className='flex items-center justify-center gap-2 mb-4'>
                  <div className='w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center'>
                     <Sparkles className='w-5 h-5 text-primary' />
                  </div>
               </div>
               <h1 className='text-2xl font-semibold tracking-tight'>
                  Accept Coach Invitation
               </h1>
               <p className='text-sm text-muted-foreground'>
                  Complete your profile to join the Evooluir coaching team
               </p>
            </div>

            <Card>
               <CardHeader className='space-y-1'>
                  <CardTitle className='text-xl'>Set up your account</CardTitle>
                  <CardDescription>
                     Create your password and complete your profile information
                  </CardDescription>
               </CardHeader>
               <CardContent>
                  <Form {...form}>
                     <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className='space-y-4'>
                        <FormField
                           control={form.control}
                           name='name'
                           render={({ field }) => (
                              <FormItem>
                                 <FormLabel>Full Name</FormLabel>
                                 <FormControl>
                                    <Input
                                       placeholder='John Doe'
                                       data-testid='input-name'
                                       {...field}
                                    />
                                 </FormControl>
                                 <FormMessage />
                              </FormItem>
                           )}
                        />

                        <FormItem>
                           <FormLabel>Email</FormLabel>
                           <FormControl>
                              <Input
                                 value={invitationData?.email || ''}
                                 readOnly
                                 disabled
                                 className='bg-muted cursor-not-allowed'
                                 data-testid='input-email'
                              />
                           </FormControl>
                           <p className='text-xs text-muted-foreground'>
                              This email is associated with your invitation
                           </p>
                        </FormItem>

                        <FormItem>
                           <FormLabel>Coach Type</FormLabel>
                           <FormControl>
                              <Input
                                 value={
                                    coachTypeLabels[
                                       invitationData?.coachType || 'coach'
                                    ] ||
                                    invitationData?.coachType ||
                                    'Coach'
                                 }
                                 readOnly
                                 disabled
                                 className='bg-muted cursor-not-allowed'
                                 data-testid='input-coach-type'
                              />
                           </FormControl>
                           <p className='text-xs text-muted-foreground'>
                              This is the role you have been invited for
                           </p>
                        </FormItem>

                        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                           <FormField
                              control={form.control}
                              name='password'
                              render={({ field }) => (
                                 <FormItem>
                                    <FormLabel>Password</FormLabel>
                                    <FormControl>
                                       <div className='relative flex items-center'>
                                          <Input
                                             type={
                                                showPassword
                                                   ? 'text'
                                                   : 'password'
                                             }
                                             placeholder='Enter your password'
                                             className='pr-10'
                                             data-testid='input-password'
                                             {...field}
                                          />
                                          <button
                                             type='button'
                                             className='absolute right-3 text-muted-foreground hover:text-foreground transition-colors'
                                             onClick={() =>
                                                setShowPassword(!showPassword)
                                             }
                                             data-testid='button-toggle-password'>
                                             {showPassword ? (
                                                <EyeOff className='h-4 w-4' />
                                             ) : (
                                                <Eye className='h-4 w-4' />
                                             )}
                                          </button>
                                       </div>
                                    </FormControl>
                                    <FormDescription className='text-xs'>
                                       {PASSWORD_HINT}
                                    </FormDescription>
                                    <FormMessage />
                                 </FormItem>
                              )}
                           />

                           <FormField
                              control={form.control}
                              name='confirmPassword'
                              render={({ field }) => (
                                 <FormItem>
                                    <FormLabel>Confirm Password</FormLabel>
                                    <FormControl>
                                       <div className='relative flex items-center'>
                                          <Input
                                             type={
                                                showConfirmPassword
                                                   ? 'text'
                                                   : 'password'
                                             }
                                             placeholder='Confirm your password'
                                             className='pr-10'
                                             data-testid='input-confirm-password'
                                             {...field}
                                          />
                                          <button
                                             type='button'
                                             className='absolute right-3 text-muted-foreground hover:text-foreground transition-colors'
                                             onClick={() =>
                                                setShowConfirmPassword(
                                                   !showConfirmPassword,
                                                )
                                             }
                                             data-testid='button-toggle-confirm-password'>
                                             {showConfirmPassword ? (
                                                <EyeOff className='h-4 w-4' />
                                             ) : (
                                                <Eye className='h-4 w-4' />
                                             )}
                                          </button>
                                       </div>
                                    </FormControl>
                                    <FormMessage />
                                 </FormItem>
                              )}
                           />
                        </div>

                        <FormField
                           control={form.control}
                           name='avatar'
                           render={({ field }) => (
                              <FormItem>
                                 <FormLabel>Profile Photo (Optional)</FormLabel>
                                 <FormControl>
                                    <div className='space-y-2'>
                                       <div className='flex items-center gap-4'>
                                          <Input
                                             ref={fileInputRef}
                                             type='file'
                                             accept='image/*'
                                             onChange={handleAvatarChange}
                                             className='hidden'
                                             id='avatar-upload'
                                             data-testid='input-avatar'
                                          />
                                          <Button
                                             type='button'
                                             variant='outline'
                                             className='flex items-center gap-2'
                                             onClick={() =>
                                                fileInputRef.current?.click()
                                             }>
                                             <Upload className='h-4 w-4' />
                                             Upload Photo
                                          </Button>
                                          {avatarPreview && (
                                             <img
                                                src={avatarPreview}
                                                alt='Avatar preview'
                                                className='w-16 h-16 rounded-full object-cover'
                                             />
                                          )}
                                       </div>
                                    </div>
                                 </FormControl>
                                 <FormMessage />
                              </FormItem>
                           )}
                        />

                        <FormField
                           control={form.control}
                           name='bio'
                           render={({ field }) => (
                              <FormItem>
                                 <FormLabel>Bio</FormLabel>
                                 <FormControl>
                                    <Textarea
                                       placeholder='Tell us about yourself...'
                                       rows={3}
                                       data-testid='input-bio'
                                       {...field}
                                    />
                                 </FormControl>
                                 <FormMessage />
                              </FormItem>
                           )}
                        />

                        <div className='space-y-4 pt-4 border-t'>
                           <div>
                              <h3 className='text-lg font-semibold mb-4'>
                                 Location
                              </h3>
                              <p className='text-sm text-muted-foreground mb-4'>
                                 Please provide your location details
                              </p>
                           </div>

                           <FormField
                              control={form.control}
                              name='country'
                              render={({ field }) => (
                                 <FormItem>
                                    <FormLabel>Country</FormLabel>
                                    <FormControl>
                                       <Select
                                          value={field.value || ''}
                                          onValueChange={(value) => {
                                             const country = countries.find(
                                                (c) => c.name === value,
                                             );
                                             setSelectedCountry(
                                                country || null,
                                             );
                                             field.onChange(value);
                                             form.setValue('state', '');
                                             form.setValue('city', '');
                                          }}>
                                          <SelectTrigger data-testid='select-country'>
                                             <SelectValue placeholder='Select country' />
                                          </SelectTrigger>
                                          <SelectContent>
                                             {countriesLoading ? (
                                                <div className='flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground'>
                                                   <Loader2 className='h-4 w-4 animate-spin' />
                                                   Loading countries…
                                                </div>
                                             ) : countries.length === 0 ? (
                                                <div className='py-4 text-center text-sm text-muted-foreground'>
                                                   No countries available
                                                </div>
                                             ) : (
                                                countries.map((country) => (
                                                   <SelectItem
                                                      key={country.id}
                                                      value={country.name}>
                                                      {country.emoji}{' '}
                                                      {country.name}
                                                   </SelectItem>
                                                ))
                                             )}
                                          </SelectContent>
                                       </Select>
                                    </FormControl>
                                    <FormMessage />
                                 </FormItem>
                              )}
                           />

                           <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                              <FormField
                                 control={form.control}
                                 name='state'
                                 render={({ field }) => (
                                    <FormItem>
                                       <FormLabel>State/Province</FormLabel>
                                       <FormControl>
                                          <Select
                                             value={field.value || ''}
                                             onValueChange={(value) => {
                                                const state = states.find(
                                                   (s) => s.name === value,
                                                );
                                                setSelectedState(state || null);
                                                field.onChange(value);
                                                form.setValue('city', '');
                                             }}
                                             disabled={!selectedCountry}>
                                             <SelectTrigger data-testid='select-state'>
                                                <SelectValue placeholder='Select state/province' />
                                             </SelectTrigger>
                                             <SelectContent>
                                                {statesLoading ? (
                                                   <div className='flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground'>
                                                      <Loader2 className='h-4 w-4 animate-spin' />
                                                      Loading states…
                                                   </div>
                                                ) : states.length === 0 ? (
                                                   <div className='py-4 text-center text-sm text-muted-foreground'>
                                                      No states available
                                                   </div>
                                                ) : (
                                                   states.map((state) => (
                                                      <SelectItem
                                                         key={state.id}
                                                         value={state.name}>
                                                         {state.name}
                                                      </SelectItem>
                                                   ))
                                                )}
                                             </SelectContent>
                                          </Select>
                                       </FormControl>
                                       <FormMessage />
                                    </FormItem>
                                 )}
                              />

                              <FormField
                                 control={form.control}
                                 name='city'
                                 render={({ field }) => (
                                    <FormItem>
                                       <FormLabel>City</FormLabel>
                                       <FormControl>
                                          <Select
                                             value={field.value || ''}
                                             onValueChange={(value) => {
                                                const city = cities.find(
                                                   (c) => c.name === value,
                                                );
                                                setSelectedCity(city || null);
                                                field.onChange(value);
                                             }}
                                             disabled={!selectedState}>
                                             <SelectTrigger data-testid='select-city'>
                                                <SelectValue placeholder='Select city' />
                                             </SelectTrigger>
                                             <SelectContent>
                                                {citiesLoading ? (
                                                   <div className='flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground'>
                                                      <Loader2 className='h-4 w-4 animate-spin' />
                                                      Loading cities…
                                                   </div>
                                                ) : cities.length === 0 ? (
                                                   <div className='py-4 text-center text-sm text-muted-foreground'>
                                                      No cities available
                                                   </div>
                                                ) : (
                                                   cities.map((city) => (
                                                      <SelectItem
                                                         key={city.id}
                                                         value={city.name}>
                                                         {city.name}
                                                      </SelectItem>
                                                   ))
                                                )}
                                             </SelectContent>
                                          </Select>
                                       </FormControl>
                                       <FormMessage />
                                    </FormItem>
                                 )}
                              />
                           </div>
                        </div>

                        <div className='space-y-4 pt-4 border-t'>
                           <FormField
                              control={form.control}
                              name='expertise'
                              render={() => (
                                 <FormItem>
                                    <div className='mb-4'>
                                       <FormLabel className='text-base'>
                                          Expertise
                                       </FormLabel>
                                       <p className='text-sm text-muted-foreground'>
                                          Select your areas of expertise
                                       </p>
                                    </div>
                                    <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3'>
                                       {expertiseOptions.map((item) => (
                                          <FormField
                                             key={item}
                                             control={form.control}
                                             name='expertise'
                                             render={({ field }) => {
                                                return (
                                                   <FormItem
                                                      key={item}
                                                      className='flex flex-row items-start space-x-3 space-y-0'>
                                                      <FormControl>
                                                         <Checkbox
                                                            checked={field.value?.includes(
                                                               item,
                                                            )}
                                                            onCheckedChange={(
                                                               checked,
                                                            ) => {
                                                               return checked
                                                                  ? field.onChange(
                                                                       [
                                                                          ...(field.value ||
                                                                             []),
                                                                          item,
                                                                       ],
                                                                    )
                                                                  : field.onChange(
                                                                       field.value?.filter(
                                                                          (
                                                                             value,
                                                                          ) =>
                                                                             value !==
                                                                             item,
                                                                       ),
                                                                    );
                                                            }}
                                                         />
                                                      </FormControl>
                                                      <FormLabel className='font-normal text-sm'>
                                                         {item}
                                                      </FormLabel>
                                                   </FormItem>
                                                );
                                             }}
                                          />
                                       ))}
                                    </div>
                                    <FormMessage />
                                 </FormItem>
                              )}
                           />
                        </div>

                        <div className='space-y-4 pt-4 border-t'>
                           <FormField
                              control={form.control}
                              name='languages'
                              render={() => (
                                 <FormItem>
                                    <div className='mb-4'>
                                       <FormLabel className='text-base'>
                                          Languages
                                       </FormLabel>
                                       <p className='text-sm text-muted-foreground'>
                                          Select languages you speak
                                       </p>
                                    </div>
                                    <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3'>
                                       {languageOptions.map((item) => (
                                          <FormField
                                             key={item.code}
                                             control={form.control}
                                             name='languages'
                                             render={({ field }) => {
                                                return (
                                                   <FormItem
                                                      key={item.code}
                                                      className='flex flex-row items-start space-x-3 space-y-0'>
                                                      <FormControl>
                                                         <Checkbox
                                                            checked={field.value?.includes(
                                                               item.code,
                                                            )}
                                                            onCheckedChange={(
                                                               checked,
                                                            ) => {
                                                               return checked
                                                                  ? field.onChange(
                                                                       [
                                                                          ...(field.value ||
                                                                             []),
                                                                          item.code,
                                                                       ],
                                                                    )
                                                                  : field.onChange(
                                                                       field.value?.filter(
                                                                          (
                                                                             value,
                                                                          ) =>
                                                                             value !==
                                                                             item.code,
                                                                       ),
                                                                    );
                                                            }}
                                                         />
                                                      </FormControl>
                                                      <FormLabel className='font-normal text-sm'>
                                                         {item.name}
                                                      </FormLabel>
                                                   </FormItem>
                                                );
                                             }}
                                          />
                                       ))}
                                    </div>
                                    <FormMessage />
                                 </FormItem>
                              )}
                           />
                        </div>

                        <FormField
                           control={form.control}
                           name='certifications'
                           render={({ field }) => (
                              <FormItem>
                                 <FormLabel>Certifications</FormLabel>
                                 <FormControl>
                                    <Textarea
                                       placeholder='Enter certifications (one per line)'
                                       rows={3}
                                       data-testid='input-certifications'
                                       {...field}
                                       value={
                                          Array.isArray(field.value)
                                             ? field.value.join('\n')
                                             : ''
                                       }
                                       onChange={(e) => {
                                          const lines = e.target.value
                                             .split('\n')
                                             .filter(
                                                (line) => line.trim() !== '',
                                             );
                                          field.onChange(lines);
                                       }}
                                    />
                                 </FormControl>
                                 <p className='text-xs text-muted-foreground'>
                                    Enter one certification per line
                                 </p>
                                 <FormMessage />
                              </FormItem>
                           )}
                        />

                        <Button
                           type='submit'
                           className='w-full'
                           disabled={isSubmitting}
                           data-testid='button-submit'>
                           {isSubmitting
                              ? 'Creating account...'
                              : 'Accept Invitation'}
                        </Button>
                     </form>
                  </Form>
               </CardContent>
            </Card>

            <div className='flex items-center justify-center gap-2 text-xs text-muted-foreground'>
               <Shield className='h-3 w-3' />
               <span>Protected by enterprise-grade security</span>
            </div>
         </div>
      </div>
   );
}
